import { useState, useCallback } from "react"
import { useMutation, useQuery } from "@apollo/client"
import {
  Box,
  Button,
  Card,
  HStack,
  Spinner,
  Stack,
  Text,
  VStack,
  Badge,
  Avatar,
  Table,
} from "@chakra-ui/react"
import { FaCheck, FaXmark } from "react-icons/fa6"
import { acceptInvitation, rejectInvitation } from "../../auth/auth"
import { SetActiveOrganizationDocument, GetMyPendingInvitationsDocument } from "@hydrologiq/gql-experience/graphql"
import { toaster } from "@hydrologiq/shared/chakra/Toaster"
import { useOrganizations } from "../../auth/AuthProvider"

interface Invitation {
  id: string
  organizationId: string
  email: string
  role: string
  status: string
  expiresAt: string
  inviterId: string
  organization?: {
    id: string
    name: string
    slug: string
    logo?: string | null
  } | null
}

interface PendingInvitationsProps {
  onInvitationAccepted?: () => void
  compact?: boolean
}

export const PendingInvitations = ({ onInvitationAccepted, compact = false }: PendingInvitationsProps) => {
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set())
  const { refreshOrganizations } = useOrganizations()

  const [setActiveOrganizationMutation] = useMutation(SetActiveOrganizationDocument)

  // Load invitations using GraphQL query that includes organization info
  const { data, loading, refetch } = useQuery(GetMyPendingInvitationsDocument, {
    fetchPolicy: "network-only",
    onError: (err) => {
      console.error("Failed to load invitations:", err)
      toaster.error({
        title: "Failed to load invitations",
        description: err.message || "Unknown error",
      })
    },
  })

  const invitations: Invitation[] =
    data?.getMyPendingInvitations?.map((inv) => ({
      id: inv.id,
      organizationId: inv.organizationId,
      email: inv.email,
      role: inv.role,
      status: inv.status,
      expiresAt: inv.expiresAt,
      inviterId: inv.inviterId,
      organization: inv.organization
        ? {
            id: inv.organization.id,
            name: inv.organization.name,
            slug: inv.organization.slug,
            logo: inv.organization.logo || null,
          }
        : null,
    })) || []

  const handleAccept = useCallback(
    async (invitation: Invitation) => {
      setProcessingIds((prev) => new Set(prev).add(invitation.id))
      try {
        const result = await acceptInvitation({ invitationId: invitation.id })

        if (result?.data) {
          toaster.success({
            title: "Invitation accepted",
            description: "You've joined the organization",
          })

          // Switch to the accepted organization
          await setActiveOrganizationMutation({
            variables: { organizationId: invitation.organizationId },
          })

          // Refresh organizations list
          await refreshOrganizations()

          // Refetch invitations to update the list
          await refetch()

          // Callback if provided
          if (onInvitationAccepted) {
            onInvitationAccepted()
          } else {
            // Reload page to refresh all data
            window.location.reload()
          }
        } else {
          throw new Error("Failed to accept invitation")
        }
      } catch (err: any) {
        console.error("Failed to accept invitation:", err)
        
        // Check if error is due to user already being a member
        const isDuplicateMemberError = 
          err.message?.includes("duplicate key") ||
          err.message?.includes("member_user_org_unique") ||
          err.code === "23505"
        
        if (isDuplicateMemberError) {
          // User is already a member - treat as success
          toaster.success({
            title: "Already a member",
            description: "You're already a member of this organization",
          })

          // Switch to the organization anyway
          await setActiveOrganizationMutation({
            variables: { organizationId: invitation.organizationId },
          })

          // Refresh organizations list
          await refreshOrganizations()

          // Refetch invitations to update the list
          await refetch()

          // Callback if provided
          if (onInvitationAccepted) {
            onInvitationAccepted()
          } else {
            // Reload page to refresh all data
            window.location.reload()
          }
        } else {
          toaster.error({
            title: "Failed to accept invitation",
            description: err.message || "Please try again",
          })
        }
      } finally {
        setProcessingIds((prev) => {
          const next = new Set(prev)
          next.delete(invitation.id)
          return next
        })
      }
    },
    [setActiveOrganizationMutation, refreshOrganizations, onInvitationAccepted]
  )

  const handleReject = useCallback(
    async (invitation: Invitation) => {
      setProcessingIds((prev) => new Set(prev).add(invitation.id))
      try {
        await rejectInvitation({ invitationId: invitation.id })

        toaster.success({
          title: "Invitation rejected",
        })

        // Refetch invitations to update the list
        await refetch()
      } catch (err: any) {
        console.error("Failed to reject invitation:", err)
        toaster.error({
          title: "Failed to reject invitation",
          description: err.message || "Please try again",
        })
      } finally {
        setProcessingIds((prev) => {
          const next = new Set(prev)
          next.delete(invitation.id)
          return next
        })
      }
    },
    [refetch]
  )

  if (loading) {
    return (
      <Box p={4} textAlign="center">
        <Spinner size="lg" />
        <Text mt={2} fontSize="sm" color="fg.muted">
          Loading invitations...
        </Text>
      </Box>
    )
  }

  if (invitations.length === 0) {
    return null
  }

  if (compact) {
    // Compact card view for empty state
    return (
      <VStack gap={3} align="stretch">
        {invitations.map((invitation) => {
          const org = invitation.organization
          const isProcessing = processingIds.has(invitation.id)
          const isExpired = new Date(invitation.expiresAt) < new Date()

          return (
            <Card.Root key={invitation.id} size="sm">
              <Card.Body>
                <HStack justify="space-between" align="flex-start">
                  <HStack gap={3} flex={1}>
                    <Avatar.Root size="md">
                      <Avatar.Image src={org?.logo || undefined} />
                      <Avatar.Fallback>
                        {org?.name?.charAt(0) || "?"}
                      </Avatar.Fallback>
                    </Avatar.Root>
                    <VStack align="flex-start" gap={1} flex={1} minWidth={0}>
                      <Text fontWeight="medium" truncate>
                        {org?.name || "Unknown Organization"}
                      </Text>
                      <HStack gap={2}>
                        <Badge colorPalette="blue" size="sm">
                          {invitation.role}
                        </Badge>
                        {isExpired && (
                          <Badge colorPalette="red" size="sm">
                            Expired
                          </Badge>
                        )}
                      </HStack>
                      <Text fontSize="xs" color="fg.muted">
                        Expires: {new Date(invitation.expiresAt).toLocaleDateString()}
                      </Text>
                    </VStack>
                  </HStack>
                  {!isExpired && (
                    <HStack gap={2}>
                      <Button
                        size="sm"
                        colorPalette="green"
                        onClick={() => handleAccept(invitation)}
                        loading={isProcessing}
                        disabled={isProcessing}
                      >
                        <FaCheck />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        colorPalette="red"
                        onClick={() => handleReject(invitation)}
                        loading={isProcessing}
                        disabled={isProcessing}
                      >
                        <FaXmark />
                      </Button>
                    </HStack>
                  )}
                </HStack>
              </Card.Body>
            </Card.Root>
          )
        })}
      </VStack>
    )
  }

  // Full table view for organization settings
  return (
    <Box>
      <Text fontSize="lg" fontWeight="bold" mb={4}>
        Pending Invitations ({invitations.length})
      </Text>

      <Table.Root size="sm">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader>Organization</Table.ColumnHeader>
            <Table.ColumnHeader>Role</Table.ColumnHeader>
            <Table.ColumnHeader>Expires</Table.ColumnHeader>
            <Table.ColumnHeader>Actions</Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {invitations.map((invitation) => {
            const org = invitation.organization
            const isProcessing = processingIds.has(invitation.id)
            const isExpired = new Date(invitation.expiresAt) < new Date()

            return (
              <Table.Row key={invitation.id}>
                <Table.Cell>
                  <HStack gap={2}>
                    <Avatar.Root size="xs">
                      <Avatar.Image src={org?.logo || undefined} />
                      <Avatar.Fallback>
                        {org?.name?.charAt(0) || "?"}
                      </Avatar.Fallback>
                    </Avatar.Root>
                    <Text>{org?.name || "Unknown Organization"}</Text>
                  </HStack>
                </Table.Cell>
                <Table.Cell>
                  <Badge colorPalette="blue">{invitation.role}</Badge>
                </Table.Cell>
                <Table.Cell>
                  <Text fontSize="sm">
                    {new Date(invitation.expiresAt).toLocaleDateString()}
                    {isExpired && (
                      <Badge ml={2} colorPalette="red" size="sm">
                        Expired
                      </Badge>
                    )}
                  </Text>
                </Table.Cell>
                <Table.Cell>
                  {!isExpired && (
                    <HStack gap={2}>
                      <Button
                        size="sm"
                        colorPalette="green"
                        onClick={() => handleAccept(invitation)}
                        loading={isProcessing}
                        disabled={isProcessing}
                      >
                        <FaCheck />
                        <Text ml={1}>Accept</Text>
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        colorPalette="red"
                        onClick={() => handleReject(invitation)}
                        loading={isProcessing}
                        disabled={isProcessing}
                      >
                        <FaXmark />
                        <Text ml={1}>Reject</Text>
                      </Button>
                    </HStack>
                  )}
                </Table.Cell>
              </Table.Row>
            )
          })}
        </Table.Body>
      </Table.Root>
    </Box>
  )
}

